import { useState } from "react";
import { Check, MessageCircle, ShoppingBag } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { wa, WA_MESSAGES, IMG } from "@/lib/site";

const SIZES = ["250 ml", "500 ml", "1 L"];

const HIGHLIGHTS = [
  "Made from the milk of Gir cows, an indigenous Indian breed",
  "Hand-churned using the traditional Bilona method",
  "No preservatives, no additives, no added colours",
  "Naturally rich golden texture and aroma",
  "Carefully packed in premium glass jars",
];

export default function Product({ promo }) {
  const [size, setSize] = useState("500 ml");

  return (
    <section id="product" data-testid="product-section" className="bg-cream py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-5 md:px-10">
        <div className="grid items-start gap-14 lg:grid-cols-2 lg:gap-20">
          <div className="lg:sticky lg:top-28">
            <Reveal>
              <div className="relative">
                <div
                  aria-hidden
                  className="absolute -inset-3 rounded-[32px] border border-gold/50"
                />
                <div className="group overflow-hidden rounded-[28px] shadow-[0_50px_100px_-40px_rgba(26,54,38,0.5)]">
                  <img
                    data-testid="product-image"
                    src={IMG.lifestyle}
                    alt="MAHIRA A2 Gir Cow Ghee jar styled on ivory linen with a brass spoon"
                    className="aspect-[4/5] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                </div>
                <div className="absolute -bottom-6 -right-2 rounded-2xl bg-forest px-6 py-4 text-ivory shadow-[0_25px_50px_-20px_rgba(26,54,38,0.6)] sm:-right-6">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-gold">
                    Small Batch
                  </p>
                  <p className="mt-1 font-heading text-lg">Packed with care</p>
                </div>
              </div>
            </Reveal>
          </div>

          <div>
            <Reveal>
              <p className="text-xs font-semibold uppercase tracking-[0.35em] text-gold">
                The Flagship
              </p>
              <h2 className="mt-5 font-heading text-4xl leading-[1.08] text-forest sm:text-5xl lg:text-6xl">
                Meet MAHIRA <span className="italic">A2 Gir Cow Ghee</span>
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-7 text-base leading-relaxed text-charcoal/70 md:text-lg">
                Golden, aromatic and unhurried — MAHIRA A2 Gir Cow Ghee is
                inspired by the way ghee has been made in Indian homes for
                generations, presented to a premium modern standard. One spoon
                is enough to understand why.
              </p>
            </Reveal>

            <Reveal delay={0.16}>
              <p className="mt-10 text-[11px] font-semibold uppercase tracking-[0.3em] text-forest/60">
                Available Sizes
              </p>
              <div className="mt-4 flex flex-wrap gap-3">
                {SIZES.map((s) => (
                  <button
                    key={s}
                    data-testid={`size-chip-${s.replace(/\s+/g, "").toLowerCase()}`}
                    onClick={() => setSize(s)}
                    className={`rounded-full border px-6 py-3 text-sm font-semibold transition-[background-color,color,border-color,transform] duration-300 hover:-translate-y-0.5 ${
                      size === s
                        ? "border-forest bg-forest text-ivory"
                        : "border-forest/20 bg-white/60 text-forest hover:border-gold"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
              {size === "500 ml" ? (
                <div data-testid="product-price" className="mt-4">
                  {promo?.active ? (
                    <div className="flex flex-wrap items-center gap-3">
                      <span className="font-heading text-4xl text-forest">₹850</span>
                      <span className="font-heading text-xl text-charcoal/40 line-through">
                        ₹1,150
                      </span>
                      <span
                        data-testid="product-offer-badge"
                        className="rounded-full bg-gold px-3.5 py-1.5 text-[10px] font-bold uppercase tracking-[0.16em] text-forest"
                      >
                        Independence Day · Save 26%
                      </span>
                    </div>
                  ) : (
                    <span className="font-heading text-4xl text-forest">₹1,150</span>
                  )}
                  <p className="mt-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-charcoal/50">
                    500 ml glass jar · 250 ml &amp; 1 L pricing on enquiry
                  </p>
                </div>
              ) : (
                <p data-testid="product-price" className="mt-4 font-heading text-2xl italic text-forest">
                  Price on enquiry
                  <span className="ml-3 align-middle font-body text-xs not-italic uppercase tracking-[0.2em] text-charcoal/50">
                    WhatsApp us for today&apos;s price
                  </span>
                </p>
              )}
            </Reveal>

            <Reveal delay={0.22}>
              <ul className="mt-8 space-y-3.5">
                {HIGHLIGHTS.map((h) => (
                  <li key={h} className="flex items-start gap-3 text-sm text-charcoal/75 md:text-base">
                    <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-gold-light/60">
                      <Check className="h-3 w-3 text-forest" />
                    </span>
                    {h}
                  </li>
                ))}
              </ul>
            </Reveal>

            <Reveal delay={0.28}>
              <div className="mt-10 flex flex-wrap gap-4">
                <a
                  data-testid="product-buy-now"
                  href={wa(WA_MESSAGES.order(size))}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-3 rounded-full bg-forest px-8 py-4 text-[12px] font-semibold uppercase tracking-[0.18em] text-ivory transition-[background-color,transform] duration-300 hover:-translate-y-1 hover:bg-forest-light"
                >
                  <ShoppingBag className="h-4 w-4" />
                  Buy Now
                </a>
                <a
                  data-testid="product-whatsapp-order"
                  href={wa(WA_MESSAGES.order(size))}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-3 rounded-full border border-forest/25 px-8 py-4 text-[12px] font-semibold uppercase tracking-[0.18em] text-forest transition-[background-color,border-color,transform] duration-300 hover:-translate-y-1 hover:border-gold hover:bg-gold/10"
                >
                  <MessageCircle className="h-4 w-4" />
                  WhatsApp to Order
                </a>
              </div>
            </Reveal>

            <Reveal delay={0.34}>
              <div
                data-testid="product-conversion-box"
                className="grain mt-12 rounded-3xl bg-forest p-8 text-ivory md:p-10"
              >
                <h3 className="font-heading text-3xl">Ready to Experience MAHIRA?</h3>
                <p className="mt-2 text-sm text-ivory/70">
                  Order directly through WhatsApp — quick, personal and simple.
                </p>
                <a
                  data-testid="conversion-box-whatsapp"
                  href={wa(WA_MESSAGES.order(size))}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-6 inline-flex items-center gap-3 rounded-full bg-gold px-8 py-4 text-[12px] font-bold uppercase tracking-[0.18em] text-forest transition-[background-color,transform] duration-300 hover:-translate-y-1 hover:bg-gold-light"
                >
                  <MessageCircle className="h-4 w-4" />
                  Order on WhatsApp
                </a>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
